import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface ScheduledDeletion {
  id: string;
  username: string;
  email: string;
  scheduledDeletionDate: string;
}

const AdminDataRetention: React.FC = () => {
  const [scheduledDeletions, setScheduledDeletions] = useState<ScheduledDeletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchScheduledDeletions();
  }, []);

  const fetchScheduledDeletions = async () => {
    try {
      const response = await apiClient.get('/v1/admin/compliance/scheduled-deletions');
      setScheduledDeletions(response.data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Data Retention</h1>
          <p className="admin-page-description">Loading data retention information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Data Retention</h1>
          <p className="admin-page-description">Error loading data retention information</p>
        </div>
        <div className="admin-alert danger">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Data Retention Management</h1>
        <p className="admin-page-description">
          Manage data retention policies and scheduled deletions
        </p>
      </div>

      {/* Data Retention Policies */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Data Retention Policies</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
          <div>
            <h4>Standard Retention Periods</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <strong>Adults (18+):</strong> 7 years
              </li>
              <li style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <strong>Minors (Under 18):</strong> 2 years
              </li>
              <li style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <strong>Inactive Accounts:</strong> 1 year
              </li>
              <li style={{ padding: '0.5rem 0' }}>
                <strong>Deleted Accounts:</strong> 30 days (anonymized)
              </li>
            </ul>
          </div>
          
          <div>
            <h4>Compliance Requirements</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <strong>GDPR (EU):</strong> Right to be forgotten
              </li>
              <li style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <strong>COPPA (US):</strong> Parental consent required
              </li>
              <li style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <strong>CCPA (California):</strong> Data portability
              </li>
              <li style={{ padding: '0.5rem 0' }}>
                <strong>Data Review:</strong> Annual compliance audit
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Scheduled Deletions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Scheduled Deletions ({scheduledDeletions.length})</h2>
        </div>
        
        {scheduledDeletions.length === 0 ? (
          <div className="admin-alert info">
            <strong>No scheduled deletions</strong> - All user data is within retention periods.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Scheduled Deletion Date</th>
                  <th>Days Remaining</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {scheduledDeletions.map((deletion) => {
                  const deletionDate = new Date(deletion.scheduledDeletionDate);
                  const today = new Date();
                  const daysRemaining = Math.ceil((deletionDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <tr key={deletion.id}>
                      <td>
                        <strong>{deletion.username}</strong>
                      </td>
                      <td>{deletion.email}</td>
                      <td>{formatDate(deletion.scheduledDeletionDate)}</td>
                      <td>
                        <span style={{ 
                          color: daysRemaining <= 7 ? '#d32f2f' : daysRemaining <= 30 ? '#f57c00' : '#2e7d32' 
                        }}>
                          {daysRemaining > 0 ? `${daysRemaining} days` : 'Overdue'}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${daysRemaining <= 0 ? 'suspended' : daysRemaining <= 7 ? 'inactive' : 'active'}`}>
                          {daysRemaining <= 0 ? 'Overdue' : daysRemaining <= 7 ? 'Urgent' : 'Scheduled'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Data Processing Guidelines */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Data Processing Guidelines</h2>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          <div>
            <h4>🔒 Data Anonymization Process</h4>
            <ol style={{ paddingLeft: '1.5rem' }}>
              <li>Replace personal identifiers with anonymized values</li>
              <li>Remove or hash sensitive data (email, phone, address)</li>
              <li>Retain non-personal data for analytics (anonymized)</li>
              <li>Update legal compliance status</li>
              <li>Generate audit log entry</li>
            </ol>
          </div>
          
          <div>
            <h4>⏰ Retention Review Process</h4>
            <ol style={{ paddingLeft: '1.5rem' }}>
              <li>Automated monthly review of all user accounts</li>
              <li>Check retention period against last activity</li>
              <li>Generate notifications for upcoming expirations</li>
              <li>Schedule deletions for expired accounts</li>
              <li>Generate compliance reports</li>
            </ol>
          </div>
          
          <div>
            <h4>📋 Compliance Checklist</h4>
            <ul style={{ listStyle: 'none', paddingLeft: '0' }}>
              <li style={{ padding: '0.25rem 0' }}>✅ Age verification implemented</li>
              <li style={{ padding: '0.25rem 0' }}>✅ Parental consent tracking</li>
              <li style={{ padding: '0.25rem 0' }}>✅ GDPR consent management</li>
              <li style={{ padding: '0.25rem 0' }}>✅ Data export functionality</li>
              <li style={{ padding: '0.25rem 0' }}>✅ Right to be forgotten</li>
              <li style={{ padding: '0.25rem 0' }}>✅ Automated retention policies</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Notice */}
      <div className="admin-alert info">
        <strong>Legal Notice:</strong> Data retention and deletion policies must comply with applicable laws in all jurisdictions where users are located. 
        Consult with legal counsel to ensure compliance with local regulations including GDPR, COPPA, CCPA, and other privacy laws.
      </div>
    </div>
  );
};

export default AdminDataRetention;